import { useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { MessageStateAtom, ServerMessage } from "../store";

/**
 * Interface defining the functionality provided by the useStreamingMessageHandler hook
 */
interface UseStreamingMessageHandler {
  /** Handles and processes incoming message strings from the server */
  handleMessage: (message: string) => void;
}

/**
 * Hook that provides handling for streaming messages received from the server
 * Parses incoming JSON messages and adds them to the message state
 * Also handles periodic cleanup of old messages
 * 
 * @returns An object with a method to handle incoming messages
 * 
 * @example
 * // Example usage
 * const { handleMessage } = useStreamingMessageHandler();

* connection.on("ReceiveMessage", handleMessage);
 */
export const useStreamingMessageHandler = (): UseStreamingMessageHandler => {
  const messageQueue = useRef<ServerMessage[]>([]);

  const [_, setMessageState] = useAtom(MessageStateAtom);

  /**
   * Handles an incoming message string from the server
   * Parses the JSON message and adds it to the message state with a received timestamp
   *
   * @param message - The JSON string message received from the server
   */
  const handleMessage = useCallback((message: string) => {
    try {
      const messageObject = JSON.parse(message) as ServerMessage;
      messageQueue.current.push(messageObject);
    } catch (error) {
      console.error("Error parsing message", error);
    }
  }, []);

  const processQueue = useCallback(() => {
    if (messageQueue.current.length > 0) {
      const batch = messageQueue.current;
      messageQueue.current = [];

      setMessageState((prev) => {
        return [
          ...prev,
          ...batch.map((x) => ({ ...x, recievedTime: new Date() })),
        ];
      });
    }
  }, []);

  /**
   * Periodically cleans up old messages to prevent memory issues
   * Currently just schedules itself to run again in 1 minute
   * TODO: Implement actual message cleanup logic
   */
  const cleanUp = useCallback(() => {
    // TODO -  setMessageState({ messages: [] }); change delete messages older than x
    setTimeout(() => {
      cleanUp();
    }, 1000 * 60);
  }, []);

  // Initialize the cleanup process when the component mounts
  useEffect(() => {
    setInterval(() => {
      processQueue();
    }, 1500); // every 1.5 seconds

    setTimeout(() => {
      cleanUp();
    }, 2000);
  }, []);

  return { handleMessage };
};
